import React, { useEffect } from "react";
import { useForm, Controller, useFieldArray } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { step1Schema } from "../../schemas/experienceSchema";

import InputField from "../../components/common/InputField";
import CustomSelect from "../../components/common/CustomSingleSelect";
import GoogleLocationAutocomplete from "../common/GoogleLocationAutocomplete";
import { TrashIcon } from "@heroicons/react/24/outline";
import { toast } from "react-toastify";

const BASE_DURATION = 30; // minutes
const BASE_GROUP_SIZE = 6;
const BUFFER_TIME = 15; // minutes

export default function StepOne({
  defaultValues,
  onNext,
  categories = [],
  isLoadingCategories = false,
  onSubmit,
  status,
}) {
  const {
    register,
    handleSubmit,
    setValue,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(step1Schema),
    defaultValues: {
      title: defaultValues?.title || "",
      location: defaultValues?.location || null,
      description: defaultValues?.description || "",
      category: defaultValues?.categoryId || "",
      price: defaultValues?.price || "",
      difficulty: defaultValues?.difficulty || "",
      importantNotes: defaultValues?.importantNotes || "",
      isPrivate: defaultValues?.isPrivate ?? false,
      maxGuestCount: defaultValues?.maxGuestCount ?? "",

      // 🔒 Fixed system values
      baseDuration: BASE_DURATION,
      baseGroupSize: BASE_GROUP_SIZE,
      bufferTime: BUFFER_TIME,

      addOns: defaultValues?.addsOn,
    },
  });

  /* =========================
     ADD-ONS FIELD ARRAY
     ========================= */
  const {
    fields: addOnFields,
    append: addAddOn,
    remove: removeAddOn,
    replace: replaceAddOns, // 👈 IMPORTANT
  } = useFieldArray({
    control,
    name: "addOns",
  });

  /* =========================
     EDIT MODE POPULATION
     ========================= */

  useEffect(() => {
    if (defaultValues?.title) {
      const matchedCategory = categories.find(
        (cat) => cat.name === defaultValues.category,
      );

      const mappedAddOns =
        defaultValues?.addOns?.map((addon) => ({
          name: addon.name || "",
          price: addon.price || "",
        })) || [];

      reset({
        title: defaultValues?.title || "",
        location: defaultValues?.location || null,
        description: defaultValues?.description || "",
        category: matchedCategory?.categoryId || matchedCategory?.id || "",
        price: defaultValues?.price || "",
        difficulty: defaultValues?.difficulty || "",
        importantNotes: defaultValues?.importantNotes || "",

        baseDuration: BASE_DURATION,
        baseGroupSize: BASE_GROUP_SIZE,
        bufferTime: BUFFER_TIME,
        maxGuestCount: defaultValues?.maxGuestCount || "",
        isPrivate: defaultValues?.isPrivate ?? false,
        addOns: mappedAddOns, // 👈 still pass
      });

      setValue("isPrivate", Boolean(defaultValues?.isPrivate));

      // 🔥 THIS is the missing piece
      replaceAddOns(mappedAddOns);
    }
  }, [defaultValues, categories, reset, replaceAddOns]);

  /* =========================
     CATEGORY OPTIONS
     ========================= */
  const categoryOptions = categories.map((cat) => ({
    value: cat.categoryId || cat.id,
    label: cat.name,
  }));

  /* =========================
     SUBMIT STEP
     ========================= */
  const submitStep = (data) => {
    const selectedCategory = categories.find(
      (cat) => (cat.categoryId || cat.id) === data.category,
    );

    if (!data?.isPrivate) {
      // maxGuestCount validation
      if (!data.maxGuestCount || data.maxGuestCount <= 0) {
        toast.error(
          "Max guest capacity must be greater than 0 or make the experience private",
        );
        return;
      }
    }

    const cleanedAddOns =
      data.addOns?.filter(
        (a) =>
          a?.name?.trim() !== "" && a?.price !== null && Number(a.price) > 0,
      ) || [];

    const stepData = {
      title: data.title,
      description: data.description,
      price: data.price,
      difficulty: data.difficulty,
      importantNotes: data.importantNotes,

      // 🔒 Fixed config
      baseDuration: BASE_DURATION,
      baseGroupSize: BASE_GROUP_SIZE,
      bufferTime: BUFFER_TIME,

      addOns: cleanedAddOns,
      location: data.location,

      category: selectedCategory?.name || "",
      categoryId: selectedCategory?.categoryId || selectedCategory?.id,
      categoryName: selectedCategory?.name || "",
      isPrivate: data.isPrivate,
      maxGuestCount: data.isPrivate ? null : Number(data.maxGuestCount),
    };

    onNext(stepData);
  };

  return (
    <div className="w-full max-w-6xl mx-auto px-4 sm:px-0">
      <form
        onSubmit={handleSubmit(submitStep)}
        className="mt-6 sm:mt-10 bg-white border border-gray-300 rounded-2xl
      px-4 sm:px-6 md:px-10 py-6 sm:py-8 md:py-12 shadow-sm"
      >
        {/* ROW 1 — TITLE + LOCATION */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <InputField
            label="Experience Title"
            name="title"
            register={register}
            bg="#F7F9FA"
            error={errors.title?.message}
          />

          <div className="w-full flex flex-col gap-1">
            <label className="font-medium text-gray-800">Location</label>
            <Controller
              name="location"
              control={control}
              render={({ field }) => (
                <GoogleLocationAutocomplete
                  value={field.value}
                  onChange={field.onChange}
                  error={errors.location?.message}
                />
              )}
            />
          </div>
        </div>

        {/* ROW 2 — DESCRIPTION (FULL WIDTH) */}
        <div className="mt-8">
          <label className="font-semibold block mb-2">Description</label>
          <textarea
            {...register("description")}
            className="w-full h-48 bg-[#F7F9FA] border rounded-xl px-4 py-3
          focus:outline-none focus:ring-2 focus:ring-[#1FADC6]"
          />
        </div>

        {/* PRIVATE BOOKING TOGGLE (TOP) */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-8">
          {/* DIFFICULTY */}
          <Controller
            name="difficulty"
            control={control}
            render={({ field }) => (
              <CustomSelect
                label="Difficulty"
                value={field.value}
                onChange={field.onChange}
                options={[
                  { value: "Easy", label: "Easy" },
                  { value: "Moderate", label: "Medium" },
                  { value: "Hard", label: "Hard" },
                ]}
                error={errors.difficulty?.message}
              />
            )}
          />

          <span className="mt-1">
            <InputField
              label="Max guest capacity"
              name="maxGuestCount"
              type="number"
              register={register}
              min={1}
              bg="#F7F9FA"
              disabled={watch("isPrivate")}
            />

            {/* PRIVATE BOOKING – INPUT STYLE MATCH */}
          </span>

          <span className="mt-1.5">
            <div className="w-full">
              {/* label exactly like others */}
              <label className="block font-medium text-gray-800 mb-2">
                Private booking (buyout)
              </label>

              {/* fake input container */}
              <div
                className="
        h-10
        px-3
        rounded-lg
       
        flex
        items-center
      "
              >
                <Controller
                  name="isPrivate"
                  control={control}
                  render={({ field }) => (
                    <label className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={field.value}
                        onChange={(e) => field.onChange(e.target.checked)}
                        className="h-4 w-4 accent-[#1FADC6]"
                      />
                      <span className="text-sm text-gray-700">
                        One booking reserves entire slot
                      </span>
                    </label>
                  )}
                />
              </div>
            </div>
          </span>

          {/* MAX GUEST */}
        </div>

        {/* ROW 3 — BASE PRICE + CATEGORY */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-8">
          <span className="mt-1">
            <InputField
              label="Base Price"
              name="price"
              type="number"
              register={register}
              bg="#F7F9FA"
              error={errors.price?.message}
            />
          </span>

          <Controller
            name="category"
            control={control}
            render={({ field }) => (
              <CustomSelect
                label="Category"
                value={field.value}
                onChange={field.onChange}
                options={categoryOptions}
                disabled={isLoadingCategories}
                error={errors.category?.message}
              />
            )}
          />
        </div>

        {/* ROW 4 — DIFFICULTY + MAX GUEST */}

        {/* ROW 5 — FIXED CONFIG */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 mt-10">
          <InputField
            label="Base Duration (minutes)"
            name="baseDuration"
            type="number"
            register={register}
            disabled
            bg="#F1F5F9"
          />
          <InputField
            label="Base Group Size"
            name="baseGroupSize"
            type="number"
            register={register}
            disabled
            bg="#F1F5F9"
          />
          <InputField
            label="Buffer Time (minutes)"
            name="bufferTime"
            type="number"
            register={register}
            disabled
            bg="#F1F5F9"
          />
        </div>

        {/* ADD ONS — SAME AS BEFORE */}
        <div className="mt-10">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-semibold text-gray-900">
              Optional Add-ons
            </h4>
            <button
              type="button"
              onClick={() => addAddOn({ name: "", price: "" })}
              className="text-sm font-medium text-[#1FADC6] hover:underline"
            >
              + Add Add-on
            </button>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {addOnFields.map((item, index) => (
              <div
                key={item.id}
                className="grid gap-2 items-center grid-cols-1 sm:grid-cols-[1fr_80px_auto] bg-gray-50 border border-gray-200 rounded-lg px-3 py-2"
              >
                <input
                  {...register(`addOns.${index}.name`)}
                  placeholder="Name"
                  className="h-8 px-2 text-sm rounded-md border border-gray-300
                focus:outline-none focus:ring-2 focus:ring-[#1FADC6]"
                />

                <div className="flex gap-2 sm:contents">
                  <input
                    type="number"
                    {...register(`addOns.${index}.price`)}
                    placeholder="Price"
                    className="
      h-8 px-2 text-sm rounded-md border border-gray-300
      focus:outline-none focus:ring-2 focus:ring-[#1FADC6]
      flex-1
    "
                  />

                  <button
                    type="button"
                    onClick={() => removeAddOn(index)}
                    className="
      h-8 w-8 flex items-center justify-center rounded-md
      text-red-500 hover:bg-red-50
    "
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* IMPORTANT NOTES — FULL WIDTH */}
        <div className="mt-8">
          <textarea
            {...register("importantNotes")}
            placeholder="Important notes (optional)"
            className="w-full h-32 bg-[#F7F9FA] border rounded-xl px-4 py-3
          focus:outline-none focus:ring-2 focus:ring-[#1FADC6]"
          />
        </div>

        {/* SUBMIT */}
        <div className="flex justify-end mt-10">
          <button
            type="submit"
            className="px-8 py-3 bg-[#1FADC6] text-white rounded-lg"
          >
            Continue
          </button>
        </div>
      </form>
    </div>
  );
}
